import React, { useEffect } from 'react'

import { Typography } from 'antd'
import { UploadProps } from 'antd/lib/upload'

import { observer } from 'mobx-react-lite'

import { Attachments as AttachmentsComponent } from 'common/components/Attachments'
import { Box } from 'common/components/boxes'
import { Card } from 'common/components/Card'
import { FileControl } from 'common/components/uploader'
import UploaderModal from 'common/components/uploaderModal'
import { File } from 'common/server/server_types'

import { Order } from 'vendor/server/orders'

type AttachmentsProps = {
  order: Order
  accept?: string
  onChange: (isDirty: boolean) => void
  addExistingFiles: (key: string, file: File[] | File) => void
  resetAllUploads: () => void
  fileList: (uploadKey: string) => UploadProps['fileList']
  disabled?: boolean
} & Omit<FileControl, 'fileList' | 'uploadKey'>

export const Attachments = observer((props: AttachmentsProps) => {
  const {
    accept,
    order,
    resetAllUploads,
    addExistingFiles,
    onChange,
    onRemoveUpload,
    onAddNewUpload,
    onResetUploads,
    onSetUploadError,
    onUpdateUpload,
    fileList,
    disabled,
  } = props
  const { public_purchaser_files, purchase_order, quote, vendor_files } = order

  useEffect(() => {
    resetAllUploads()
    addExistingFiles('vendor_files', vendor_files)
    addExistingFiles('public_purchaser_files', public_purchaser_files)
    addExistingFiles('purchase_order', purchase_order)
    addExistingFiles('quote', quote)
  }, [resetAllUploads, addExistingFiles, vendor_files, public_purchaser_files, purchase_order, quote])

  const commonProps = {
    onRemoveUpload,
    onAddNewUpload,
    onResetUploads,
    onSetUploadError,
    onUpdateUpload,
    onChange: () => onChange(true),
    noResetUploads: true,
  }

  return (
    <Card title="Attachments" contentProps={{ display: 'flex', flexDirection: { _: 'column', sm: 'row' } }}>
      <Box display="flex" flexDirection="column" width={{ _: '100%', sm: '50%' }}>
        <Typography.Paragraph strong>My Attachments</Typography.Paragraph>

        <Typography.Text strong>Quote</Typography.Text>
        <AttachmentsComponent
          isResponsive={false}
          data-cy="upload-quote"
          multiple={false}
          accept={accept}
          uploadKey="quote"
          fileList={fileList('quote')}
          disabled={disabled}
          {...commonProps}
        />

        <Box mt={12}>
          <UploaderModal
            data-cy="upload-vendor-file"
            title="Other Attachments"
            multiple
            uploadKey="vendor_files"
            onChange={() => onChange(true)}
            fileList={fileList('vendor_files')}
            onRemoveUpload={onRemoveUpload}
            onAddNewUpload={onAddNewUpload}
            onResetUploads={onResetUploads}
            onSetUploadError={onSetUploadError}
            onUpdateUpload={onUpdateUpload}
            uploadButtonText="Upload"
            disabled={disabled}
          />
        </Box>
      </Box>

      <Box
        flexGrow={1}
        mx={{ _: 0, sm: 12 }}
        my={{ _: 12, sm: 0 }}
        borderRightStyle="solid"
        borderRightWidth={{ _: 'none', sm: '1px' }}
        borderBottomWidth={{ _: '1px', sm: 'none' }}
        borderRightColor="gray-4"
        borderBottomColor="gray-4"
      />

      <Box display="flex" flexDirection="column" width={{ _: '100%', sm: '50%' }}>
        <Typography.Paragraph strong>Customer Attachments</Typography.Paragraph>
        <UploaderModal
          multiple={false}
          uploadKey="purchase_order"
          title="Purchase Order"
          disabled
          fileList={fileList('purchase_order')}
        />
        <Box mt={12}>
          <UploaderModal
            multiple={true}
            uploadKey="public_purchaser_files"
            title="More Attachments"
            disabled
            fileList={fileList('public_purchaser_files')}
          />
        </Box>
      </Box>
    </Card>
  )
})
